/**
 * The login gate.
 *
 * Phone OTP via MSG91 widget is the default. Google and email/password secondary.
 */
import { useEffect, useRef, useState } from "react";
import type { AuthUser } from "@sportsos/engine";
import { api } from "../api.ts";
import { authProblem, googleFailureFrom, type AuthMode, type AuthProblem } from "../auth/messages.ts";
import type { FrictionObserver } from "../auth/friction.ts";

declare global {
  interface Window {
    sendOtp?: (identifier: string, success?: (data: unknown) => void, failure?: (error: unknown) => void) => void;
    verifyOtp?: (otp: number | string, success?: (data: unknown) => void, failure?: (error: unknown) => void) => void;
    retryOtp?: (channel: string | null, success?: (data: unknown) => void, failure?: (error: unknown) => void) => void;
    initSendOTP?: (config: Record<string, unknown>) => void;
  }
}

const MSG91_WIDGET_ID = import.meta.env.VITE_MSG91_WIDGET_ID ?? "";
const MSG91_TOKEN_AUTH = import.meta.env.VITE_MSG91_TOKEN_AUTH ?? "";

interface Props {
  /**
   * Which of the two forms opens first.
   *
   * `/signin` and `/signup` are separate addresses (ADR-0014) and the landing page links to
   * both, so the screen cannot always start on sign-in and let people find the toggle.
   */
  readonly initialMode: AuthMode;
  readonly onSignedIn: (user: AuthUser) => void;
  readonly friction: FrictionObserver;
  readonly dark: boolean;
  readonly onToggleTheme: () => void;
  readonly onScoreAsGuest: () => void;
  readonly onBack: (() => void) | null;
}

export function SignIn({
  initialMode,
  onSignedIn,
  friction,
  dark,
  onToggleTheme,
  onScoreAsGuest,
  onBack,
}: Props): React.ReactElement {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [phone, setPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [problem, setProblem] = useState<AuthProblem | null>(null);
  const [busy, setBusy] = useState(false);
  const [widgetReady, setWidgetReady] = useState(false);
  const scriptLoaded = useRef(false);
  const onSignedInRef = useRef(onSignedIn);
  onSignedInRef.current = onSignedIn;
  const phoneRef = useRef(phone);
  phoneRef.current = phone;
  const frictionRef = useRef(friction);
  frictionRef.current = friction;

  useEffect(() => {
    friction.shown();
    const failure = googleFailureFrom(window.location.search);
    if (failure !== null) {
      friction.failure("google", failure.reason);
      setProblem({ message: failure.message, code: failure.reason, fields: {}, suggest: null });
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [friction]);

  useEffect(() => {
    if (scriptLoaded.current) return;
    scriptLoaded.current = true;

    const config = {
      widgetId: MSG91_WIDGET_ID,
      tokenAuth: MSG91_TOKEN_AUTH,
      exposeMethods: true,
      success: () => {},
      failure: () => {},
    };

    const script = document.createElement("script");
    script.src = "https://verify.msg91.com/otp-provider.js";
    script.onload = () => {
      if (window.initSendOTP) {
        window.initSendOTP(config);
        setWidgetReady(true);
      }
    };
    document.body.appendChild(script);
  }, []);

  const handleSendOtp = () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) {
      setProblem({ message: "Enter a valid 10-digit mobile number", code: "invalid_phone", fields: { phone: "e.g. 9876543210" }, suggest: null });
      return;
    }
    const mobile = `91${digits}`;
    setProblem(null);
    setBusy(true);
    friction.attempt("phone_otp");
    if (window.sendOtp) {
      window.sendOtp(
        mobile,
        () => { setOtpSent(true); setBusy(false); },
        (error) => {
          console.error("Send OTP failed", error);
          setProblem({ message: "Could not send OTP. Try again.", code: "otp_send_failed", fields: {}, suggest: null });
          setBusy(false);
        },
      );
    } else {
      setProblem({ message: "OTP service loading. Wait a moment and try again.", code: "widget_not_loaded", fields: {}, suggest: null });
      setBusy(false);
    }
  };

  const handleVerifyOtp = () => {
    if (otp.length < 4) {
      setProblem({ message: "Enter the OTP you received", code: "invalid_otp", fields: { token: "enter the code" }, suggest: null });
      return;
    }
    setProblem(null);
    setBusy(true);
    if (window.verifyOtp) {
      window.verifyOtp(
        otp,
        (data) => {
          const token = (data as { message?: string })?.message ?? "";
          if (token) {
            void (async () => {
              try {
                friction.attempt("phone_otp");
                const { user } = await api.verifyOtp({ token, phone: `91${phone}` });
                friction.succeeded("phone_otp");
                onSignedIn(user);
              } catch (error: unknown) {
                const failure = authProblem(error, mode);
                friction.failure("phone_otp", failure.code);
                setProblem(failure);
                setBusy(false);
              }
            })();
          } else {
            setProblem({ message: "Verification failed", code: "verify_failed", fields: {}, suggest: null });
            setBusy(false);
          }
        },
        (error) => {
          console.error("Verify OTP failed", error);
          setProblem({ message: "Invalid OTP. Try again.", code: "invalid_otp", fields: {}, suggest: null });
          setOtp("");
          setBusy(false);
        },
      );
    } else {
      setProblem({ message: "OTP service not loaded", code: "widget_not_loaded", fields: {}, suggest: null });
      setBusy(false);
    }
  };

  const submitEmail = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    const method = mode === "sign_up" ? "email_sign_up" : "email_sign_in";
    setBusy(true);
    setProblem(null);
    friction.attempt(method);
    try {
      const { user } =
        mode === "sign_up"
          ? await api.signUp({ email, password, displayName })
          : await api.signIn({ email, password });
      friction.succeeded(method);
      onSignedIn(user);
    } catch (error: unknown) {
      const failure = authProblem(error, mode);
      friction.failure(method, failure.code);
      setProblem(failure);
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  const google = (): void => {
    friction.attempt("google");
    window.location.assign(api.googleSignInUrl());
  };

  const signingUp = mode === "sign_up";

  return (
    <div className="ds-on-deep login-screen">
      <div className="ds-photo" style={{ height: "100%" }}>
        {/* Stadium background — same as original */}
        <svg className="ds-photo__media" viewBox="0 0 390 844" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <defs>
            <linearGradient id="cs-sky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="var(--ds-navy-950)" />
              <stop offset=".5" stopColor="var(--ds-navy-800)" />
              <stop offset="1" stopColor="var(--ds-navy-400)" />
            </linearGradient>
            <radialGradient id="cs-flood" cx=".5" cy=".1" r=".6">
              <stop offset="0" stopColor="var(--ds-ball-300)" stopOpacity=".38" />
              <stop offset="1" stopColor="var(--ds-ball-300)" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="cs-turf" cx=".5" cy="1" r=".7">
              <stop offset="0" stopColor="var(--ds-navy-300)" stopOpacity=".5" />
              <stop offset="1" stopColor="var(--ds-navy-300)" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect width="390" height="844" fill="url(#cs-sky)" />
          <g fill="var(--ds-navy-100)" opacity=".45">
            <rect x="46" y="112" width="6" height="132" rx="3" />
            <rect x="338" y="112" width="6" height="132" rx="3" />
            <rect x="22" y="82" width="54" height="34" rx="6" />
            <rect x="314" y="82" width="54" height="34" rx="6" />
          </g>
          <g fill="var(--ds-ball-300)" opacity=".5">
            <circle cx="35" cy="92" r="4" /><circle cx="49" cy="92" r="4" /><circle cx="63" cy="92" r="4" />
            <circle cx="327" cy="92" r="4" /><circle cx="341" cy="92" r="4" /><circle cx="355" cy="92" r="4" />
            <circle cx="35" cy="106" r="4" /><circle cx="49" cy="106" r="4" /><circle cx="63" cy="106" r="4" />
            <circle cx="327" cy="106" r="4" /><circle cx="341" cy="106" r="4" /><circle cx="355" cy="106" r="4" />
          </g>
          <rect width="390" height="520" fill="url(#cs-flood)" />
          <path d="M0 214 L390 214 L390 300 L0 300Z" fill="var(--ds-navy-950)" opacity=".5" />
          <g fill="var(--ds-navy-200)" opacity=".28">
            <rect x="0" y="226" width="390" height="7" rx="3.5" />
            <rect x="0" y="244" width="390" height="7" rx="3.5" />
            <rect x="0" y="262" width="390" height="7" rx="3.5" />
            <rect x="0" y="280" width="390" height="7" rx="3.5" />
          </g>
          <ellipse cx="195" cy="640" rx="330" ry="230" fill="var(--ds-navy-700)" />
          <rect width="390" height="844" fill="url(#cs-turf)" />
          <path d="M168 322 L222 322 L282 700 L108 700Z" fill="var(--ds-navy-100)" opacity=".13" />
          <g stroke="var(--ds-neutral-0)" strokeWidth="2" opacity=".28" fill="none">
            <path d="M162 356 H228" /><path d="M140 500 H250" />
          </g>
          <g fill="var(--ds-neutral-0)" opacity=".55">
            <rect x="185" y="300" width="4" height="34" rx="2" />
            <rect x="193" y="300" width="4" height="34" rx="2" />
            <rect x="201" y="300" width="4" height="34" rx="2" />
          </g>
          <path d="M318 152 q-46 44-64 118" stroke="var(--ds-white-a36)" strokeWidth="2.5" fill="none" strokeDasharray="6 8" strokeLinecap="round" />
          <circle cx="320" cy="150" r="11" fill="var(--ds-danger-500)" />
          <path d="M313 143 a11 11 0 0 1 14 14" stroke="var(--ds-neutral-0)" strokeWidth="1.6" fill="none" opacity=".7" />
        </svg>

        <div className="ds-photo__scrim" />

        <div className="ds-photo__body" style={{ padding: "54px var(--ds-space-5) var(--ds-space-8)" }}>
          {/* Logo */}
          <div className="row">
            {onBack !== null ? (
              <button className="ds-btn ds-btn--ghost ds-btn--sm" onClick={onBack}>&#8249; Back</button>
            ) : (
              // A signed-out dead end is never the last screen anyone sees.
              //
              // A real `<a>`, not a `go()` call: `/` is a server-rendered document rather than
              // a destination inside this app (ADR-0014), so a full navigation is what is
              // wanted — the SPA has nothing to render at that address and should unload.
              <a className="ds-btn ds-btn--ghost ds-btn--sm" href="/">&#8249; What is CricAI.co?</a>
            )}
            <span className="ds-icon-chip" style={{ width: 30, height: 30, background: "var(--ds-neutral-0)", border: 0, color: "var(--ds-navy-950)" }}>
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16.4 3.4a2.7 2.7 0 0 1 3.8 3.8l-7.7 7.7-3.8-3.8Z" />
                <path d="M8.7 11.1 6 13.8a1.7 1.7 0 0 0 0 2.4l1.5 1.5a1.7 1.7 0 0 0 2.4 0l2.7-2.7" />
                <circle cx="5.4" cy="6.1" r="2.3" />
              </svg>
            </span>
            <b style={{ fontSize: "var(--ds-text-lg)" }}>CricAI.co</b>
          </div>

          <div className="spacer" />

          <h1 className="ds-hero-title ds-hero-title--solid" style={{ fontSize: "var(--ds-text-3xl)" }}>
            Score every<br />ball of it.
          </h1>
          <p className="ds-hero-subtitle">
            Ball-by-ball scoring, live scorecards and one link to share the match.
          </p>

          {/* ── Phone OTP (default) ── */}
          {!showEmail && (
            <div className="stack stack--tight" style={{ marginTop: "var(--ds-space-5)" }}>
              {!otpSent ? (
                <>
                  <div className="ds-field">
                    <label className="ds-label" htmlFor="phone">Mobile number</label>
                    <div style={{ display: "flex", gap: "var(--ds-space-2)" }}>
                      <span className="ds-input" style={{
                        width: 52, flexShrink: 0, display: "flex", alignItems: "center",
                        justifyContent: "center", color: "var(--ds-text-muted)", fontWeight: 600,
                      }}>+91</span>
                      <input
                        className="ds-input"
                        id="phone"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel"
                        placeholder="9876543210"
                        maxLength={10}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                        style={{ flex: 1 }}
                      />
                    </div>
                    <Hint problem={problem} field="phone" />
                  </div>
                  <button
                    className="ds-btn ds-btn--primary ds-btn--lg ds-btn--block"
                    onClick={handleSendOtp}
                    disabled={busy || !widgetReady}
                    type="button"
                  >
                    {busy ? "Sending..." : !widgetReady ? "Loading..." : "Send OTP"}
                  </button>
                </>
              ) : (
                <>
                  <div className="ds-field">
                    <label className="ds-label" htmlFor="otp">Enter OTP</label>
                    <input
                      className="ds-input"
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="6-digit code"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    />
                    <p className="ds-hint">OTP sent to +91 {phone}</p>
                    <Hint problem={problem} field="token" />
                  </div>
                  <button
                    className="ds-btn ds-btn--primary ds-btn--lg ds-btn--block"
                    onClick={handleVerifyOtp}
                    disabled={busy}
                    type="button"
                  >
                    {busy ? "Verifying..." : "Verify OTP"}
                  </button>
                  <button
                    className="ds-btn ds-btn--ghost ds-btn--sm"
                    onClick={() => { setOtpSent(false); setOtp(""); setProblem(null); }}
                    type="button"
                  >
                    Change number
                  </button>
                </>
              )}

              {problem !== null && !problem.fields["phone"] && !problem.fields["token"] && (
                <p className="ds-hint ds-hint--error" style={{ marginTop: "var(--ds-space-2)" }}>
                  {problem.message}
                </p>
              )}

              <div style={{ margin: "var(--ds-space-3) 0" }}>
                <p className="ds-rule">or</p>
              </div>

              <button className="ds-btn ds-btn--glass ds-btn--lg ds-btn--block" onClick={google} disabled={busy} type="button">
                Continue with Google
              </button>

              <button className="ds-btn ds-btn--ghost ds-btn--sm ds-btn--block"
                onClick={() => { setShowEmail(true); setProblem(null); }} type="button"
                style={{ marginTop: "var(--ds-space-2)" }}>
                Sign in with email instead
              </button>
            </div>
          )}

          {/* ── Email/password (secondary) ── */}
          {showEmail && (
            <form onSubmit={submitEmail}>
              <div className="stack stack--tight" style={{ marginTop: "var(--ds-space-5)" }}>
                {signingUp && (
                  <div className="ds-field">
                    <label className="ds-label" htmlFor="displayName">Your name</label>
                    <input className="ds-input" id="displayName" type="text" autoComplete="name"
                      value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                    <Hint problem={problem} field="displayName" />
                  </div>
                )}
                <div className="ds-field">
                  <label className="ds-label" htmlFor="email">Email</label>
                  <input className="ds-input" id="email" type="email" autoCapitalize="none" autoCorrect="off"
                    spellCheck={false} autoComplete="email" inputMode="email"
                    value={email} onChange={(e) => setEmail(e.target.value)} />
                  <Hint problem={problem} field="email" />
                </div>
                <div className="ds-field">
                  <label className="ds-label" htmlFor="password">Password</label>
                  <input className="ds-input" id="password" type="password"
                    autoComplete={signingUp ? "new-password" : "current-password"}
                    value={password} onChange={(e) => setPassword(e.target.value)} />
                  <Hint problem={problem} field="password" />
                </div>
              </div>

              {problem !== null && (
                <p className="ds-hint ds-hint--error" style={{ marginTop: "var(--ds-space-2)" }}>{problem.message}</p>
              )}

              {problem !== null && problem.suggest !== null && (
                <SuggestSwitch suggest={problem.suggest} onSwitch={(next) => { setMode(next); setProblem(null); }} />
              )}

              <div className="stack stack--tight" style={{ marginTop: "var(--ds-space-5)" }}>
                <button className="ds-btn ds-btn--primary ds-btn--lg ds-btn--block" type="submit" disabled={busy}>
                  {busy ? "..." : signingUp ? "Create account and score" : "Sign in"}
                </button>
                <button type="button" className="ds-btn ds-btn--glass ds-btn--lg ds-btn--block"
                  onClick={() => { setMode(signingUp ? "sign_in" : "sign_up"); setProblem(null); }}>
                  {signingUp ? "I already have an account" : "Create account"}
                </button>
                <button type="button" className="ds-btn ds-btn--ghost ds-btn--sm ds-btn--block"
                  onClick={() => { setShowEmail(false); setProblem(null); }}
                  style={{ marginTop: "var(--ds-space-2)" }}>
                  Back to phone login
                </button>
              </div>
            </form>
          )}

          <p className="ds-caption" style={{ textAlign: "center", marginTop: "var(--ds-space-4)" }}>
            <b style={{ color: "var(--ds-text)", cursor: "pointer" }} role="button" tabIndex={0}
              onClick={onScoreAsGuest}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onScoreAsGuest(); }}>
              Score without an account
            </b>
            {" "}&middot; stays on this device
          </p>
        </div>
      </div>
    </div>
  );
}

function SuggestSwitch({ suggest, onSwitch }: { readonly suggest: AuthMode; readonly onSwitch: (mode: AuthMode) => void }): React.ReactElement {
  return (
    <button type="button" className="ds-btn ds-btn--glass ds-btn--block" style={{ marginTop: "var(--ds-space-2)" }}
      onClick={() => onSwitch(suggest)}>
      {suggest === "sign_in" ? "Sign in instead" : "Create an account instead"}
    </button>
  );
}

function Hint({ problem, field }: { readonly problem: AuthProblem | null; readonly field: string }): React.ReactElement | null {
  const hint = problem?.fields[field];
  return hint === undefined ? null : <p className="ds-hint ds-hint--error">{hint}</p>;
}
